import * as React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import ForgotPassword from '../sign-in/ForgotPassword';
import ApiClient from '../utils/apiClient';
import { clearUserCache } from '../utils/cacheUtils';
import { startOctopusLoadSse } from '../utils/loadOctopusSse';

const EMAIL_ID = 'landing-signin-email';
const PASSWORD_ID = 'landing-signin-password';

export interface SignInDropdownTriggerProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export interface SignInDropdownProps {
  /** Renders the trigger button; receives onClick for desktop (open dropdown) or mobile (navigate). */
  renderTrigger: (props: SignInDropdownTriggerProps) => React.ReactNode;
}

export default function SignInDropdown({ renderTrigger }: SignInDropdownProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [authError, setAuthError] = React.useState('');
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const [staySignedIn, setStaySignedIn] = React.useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = React.useState(false);

  const validate = () => {
    const email = document.getElementById(EMAIL_ID) as HTMLInputElement | null;
    const password = document.getElementById(PASSWORD_ID) as HTMLInputElement | null;
    let isValid = true;
    if (!email?.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }
    if (!password?.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError('');
    setIsSigningIn(true);
    if (!validate()) {
      setIsSigningIn(false);
      return;
    }
    const form = event.currentTarget;
    const formData = new FormData(form);
    const details = Object.fromEntries(formData);
    const loginData = { ...details, stay_signed_in: staySignedIn };
    try {
      const { data } = await axios.post(
        `${ApiClient.getCurrentBaseUrl()}/api/users/login/`,
        loginData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      clearUserCache();
      localStorage.setItem('token', data.token);
      localStorage.setItem('user_id', data.user.user_id);
      localStorage.setItem('access', data.access);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      if (data.user?.user_id) startOctopusLoadSse(data.user.user_id);
      setAnchorEl(null);
      navigate('/app', { replace: true });
    } catch (error: unknown) {
      setIsSigningIn(false);
      const res =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { status?: number } }).response
          : undefined;
      const status = res?.status;
      if (status === 401) {
        setAuthError('Login failed. Please check your username and password and try again.');
      } else if (status !== undefined && status >= 400 && status < 500) {
        setAuthError('Login failed. Something went wrong during the login process. Please try again later.');
      } else if (status !== undefined && status >= 500) {
        setAuthError('Sorry, something went wrong at our end. Please try again later.');
      } else {
        setAuthError('Login failed. Please try again.');
      }
    }
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDesktop) {
      setAnchorEl(event.currentTarget);
      setAuthError('');
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      {renderTrigger({ onClick: handleTriggerClick })}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 320, maxWidth: 380, p: 2, mt: 1.5 } } }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Sign in
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <FormControl>
            <FormLabel htmlFor={EMAIL_ID}>Email</FormLabel>
            <TextField
              error={emailError}
              helperText={emailErrorMessage}
              id={EMAIL_ID}
              type="email"
              name="email"
              placeholder="your@email.com"
              autoComplete="email"
              required
              fullWidth
              size="small"
              variant="outlined"
              color={emailError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormLabel htmlFor={PASSWORD_ID}>Password</FormLabel>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setForgotPasswordOpen(true)}
              >
                Forgot password?
              </Link>
            </Box>
            <TextField
              error={passwordError}
              helperText={passwordErrorMessage}
              name="password"
              placeholder="••••••"
              type="password"
              id={PASSWORD_ID}
              autoComplete="current-password"
              required
              fullWidth
              size="small"
              variant="outlined"
              color={passwordError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={staySignedIn}
                onChange={(e) => setStaySignedIn(e.target.checked)}
                name="staySignedIn"
              />
            }
            label="Stay signed in"
          />
          <ForgotPassword open={forgotPasswordOpen} handleClose={() => setForgotPasswordOpen(false)} />
          {authError && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {authError}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            onClick={() => validate()}
            disabled={isSigningIn}
          >
            {isSigningIn ? 'Signing in...' : 'Sign in'}
          </Button>
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            Don&apos;t have an account? <Link component={RouterLink} to="/register">Sign up</Link>
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
